import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import api from "@/lib/api";
import { toast } from "sonner";
import VolunteerCountBadge from "./VolunteerCountBadge";

const FORMS = [
  { type: "resource_table_host", label: "Host a VIP Resource Table",
    blurb: "Apply to host a speed-networking resource table at the VIP experience on July 17.",
    extras: [
      { key: "resource_type", label: "What resource will you offer?", placeholder: "e.g., grant-writing support" },
    ],
  },
  { type: "ticket_question", label: "Ticket Questions",
    blurb: "Questions about purchasing or upgrading event tickets.",
    extras: [
      { key: "ticket_type", label: "Ticket type", placeholder: "VIP, General, Group" },
    ],
  },
  { type: "volunteer", label: "Event Volunteers Needed",
    blurb: "Dates: July 17 & 18, 2026 (VIP & Business Showers Main Event). Venue: Renaissance Hotel by Marriott · 5501 Carnegie Blvd, Charlotte, NC 28209. Volunteer Orientation: Friday, July 10, 2026 · 6:30 PM ET via Zoom.",
    footnote: "Volunteer role selections indicate your preferences. Final roles and assignments may be adjusted based on event needs, staffing levels, and availability. You will receive confirmation of your approved assignment.",
    nameMode: "split",
    hideMessage: true,
    extras: [
      { key: "volunteering_with_company", label: "Are you volunteering with a company? If so, please list the name *", type: "text", required: true, placeholder: "Company name or 'No'" },
      { key: "preferred_contact_method", label: "Preferred contact method *", type: "radio", required: true, options: ["Phone", "Email"] },
      { key: "is_alumni", label: "Are you a previous Business Shower honoree? *", type: "radio", required: true, options: ["Yes", "No"] },
      { key: "alumni_year", label: "If yes, which year were you honored?", type: "text", placeholder: "e.g., 2024", showWhen: { is_alumni: "Yes" } },
      { key: "is_out_of_state", label: "Are you out-of-state / unable to attend in person? *", type: "radio", required: true, options: ["Yes", "No"] },
      { key: "primary_role", label: "Where would you like to help most? *", type: "checkboxes", required: true, options: [
        "Friday, July 17 at 8:00 AM — Setup Volunteer (4 needed)",
        "Saturday, July 18 — Business Shower Event Volunteer",
        "Remote/Admin Support Before the Event",
      ] },
      { key: "admin_task_options", anchor: "admin-tasks", label: "If Remote/Admin Support, which tasks interest you?", type: "checkboxes", options: [
        "Making confirmation calls",
        "Sending reminder emails",
        "Organizing volunteer lists",
        "Helping with check-in lists",
        "Preparing name tags or packets",
        "Reviewing sign-up forms",
        "Supporting post-event follow-up",
        "Other admin support as needed",
      ], showWhen: { primary_role_contains: "Remote/Admin Support Before the Event" } },
      { key: "vip_night_slots", label: "If attending in person — Volunteer Opportunities for VIP Night, Friday July 17, 2026", type: "checkboxes", options: [
        "Set-Up VIP Event Tables & Decorations Team · 8:00 AM–12:00 PM",
        "Event volunteers various stations · 3:00 PM–8:30 PM",
        "Set Ballroom for Business Showers · 6:30 PM–9:30 PM",
        "Tear-Down VIP Event Tables & Decorations · 8:30 PM–9:30 PM",
        "I can't volunteer this day.",
      ] },
      { key: "saturday_slots", label: "If attending in person — Volunteer Opportunities for Saturday July 18, 2026", type: "checkboxes", options: [
        "Event volunteers · 11:30 AM–5:30 PM",
        "Clean-up & Tear Down Business Showers Event · 5:30 PM–7:30 PM",
        "I can't volunteer this day.",
      ] },
      { key: "how_heard", label: "How did you hear about the Business Showers Organization? *", type: "select", required: true, options: ["Referral", "Facebook", "Instagram", "Another Organization", "LinkedIn"] },
      { key: "other_areas_interest", label: "Are you interested in volunteering in other areas for FIMBS outside of this event? If so please specify.", type: "textarea", placeholder: "Tell us what else you'd love to support…" },
    ],
  },
  { type: "media", label: "Media & Press Inquiry",
    blurb: "Press credentials, interviews and coverage.",
    extras: [
      { key: "outlet", label: "Outlet", placeholder: "Name of publication / channel" },
    ],
  },
  { type: "next_year_business", label: "Next-Year Business Interest",
    blurb: "Interested in being a future Business Showers participant?",
    extras: [
      { key: "business_stage", label: "Business stage", placeholder: "Idea, 0-1yr, 1-3yr…" },
    ],
  },
  { type: "general_question", label: "General Event Questions",
    blurb: "Anything else? We'd love to hear from you.",
    extras: [],
  },
];

function FormDialog({ open, onOpenChange, form }) {
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  // Live values of fields used in `showWhen` conditions (radios + checkboxes)
  const [fieldValues, setFieldValues] = useState({});

  if (!form) return null;

  const splitName = form.nameMode === "split";
  const hideMessage = Boolean(form.hideMessage);

  // Reset state whenever dialog opens for a new form
  const handleOpenChange = (v) => {
    if (!v) {
      setSubmitting(false);
      setSubmitted(false);
      setFieldValues({});
    }
    onOpenChange?.(v);
  };

  const trackChange = (key, type) => (e) => {
    if (type === "checkboxes") {
      // Aggregate all checked values for this key
      const root = e.target.closest("form");
      if (!root) return;
      const values = Array.from(root.querySelectorAll(`input[name='${key}']:checked`)).map((el) => el.value);
      setFieldValues((prev) => ({ ...prev, [key]: values }));
    } else {
      setFieldValues((prev) => ({ ...prev, [key]: e.target.value }));
    }
  };

  const shouldShow = (x) => {
    if (!x.showWhen) return true;
    for (const [depKey, depVal] of Object.entries(x.showWhen)) {
      if (depKey.endsWith("_contains")) {
        const baseKey = depKey.replace(/_contains$/, "");
        const arr = fieldValues[baseKey];
        if (!Array.isArray(arr) || !arr.includes(depVal)) return false;
      } else {
        if (fieldValues[depKey] !== depVal) return false;
      }
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    const fd = new FormData(e.currentTarget);
    const extras = {};
    // Pre-validate required checkbox groups (HTML5 can't natively require "at least one")
    for (const x of (form.extras || [])) {
      if (x.type === "checkboxes" && x.required) {
        const all = fd.getAll(x.key);
        if (!all.length) {
          toast.error(`Please select at least one option for "${x.label.replace(/\s*\*\s*$/, "")}"`);
          setSubmitting(false);
          return;
        }
      }
    }
    (form.extras || []).forEach((x) => {
      if (x.type === "checkboxes") {
        const all = fd.getAll(x.key);
        if (all.length) extras[x.key] = all.join("; ");
      } else {
        const v = fd.get(x.key);
        if (v) extras[x.key] = v;
      }
    });
    // Resolve the name field — either combined or split-then-combined
    let name;
    if (splitName) {
      const first = (fd.get("first_name") || "").toString().trim();
      const last = (fd.get("last_name") || "").toString().trim();
      name = [first, last].filter(Boolean).join(" ");
      if (first) extras.first_name = first;
      if (last) extras.last_name = last;
    } else {
      name = fd.get("name");
    }
    try {
      await api.post("/submissions", {
        form_type: form.type,
        name,
        email: fd.get("email"),
        phone: fd.get("phone") || "",
        organization: fd.get("organization") || "",
        message: fd.get("message") || "",
        extra: extras,
        source: "website",
      });
      setSubmitted(true);
      toast.success("Thank you! Check your email for confirmation.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Submission failed. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const renderExtra = (x) => {
    const inputCls = "mt-1 bg-white border-emerald-200 focus-visible:ring-emerald-500/50";
    if (x.type === "textarea") {
      return (
        <Textarea
          data-testid={`form-extra-${x.key}`}
          name={x.key}
          required={x.required}
          placeholder={x.placeholder}
          rows={3}
          className={inputCls}
        />
      );
    }
    if (x.type === "select") {
      return (
        <select
          data-testid={`form-extra-${x.key}`}
          name={x.key}
          required={x.required}
          defaultValue=""
          className={`${inputCls} w-full h-10 rounded-md px-3 py-2 text-sm font-[Outfit] border`}
        >
          <option value="" disabled>Choose…</option>
          {(x.options || []).map((opt) => (
            <option key={opt} value={opt}>{opt}</option>
          ))}
        </select>
      );
    }
    if (x.type === "radio") {
      return (
        <div className="mt-1 flex flex-wrap gap-3" role="radiogroup" data-testid={`form-extra-${x.key}`}>
          {(x.options || []).map((opt) => (
            <label key={opt} className="inline-flex items-center gap-2 bg-white border border-emerald-200 rounded-full px-4 py-2 cursor-pointer hover:border-emerald-400 font-[Outfit] text-sm">
              <input type="radio" name={x.key} value={opt} required={x.required} className="accent-emerald-700" onChange={trackChange(x.key, "radio")} />
              {opt}
            </label>
          ))}
        </div>
      );
    }
    if (x.type === "checkboxes") {
      return (
        <div className="mt-1 grid gap-2" data-testid={`form-extra-${x.key}`}>
          {(x.options || []).map((opt) => (
            <label key={opt} className="flex items-start gap-2 bg-white border border-emerald-200 rounded-md px-3 py-2 cursor-pointer hover:border-emerald-400 font-[Outfit] text-sm">
              <input type="checkbox" name={x.key} value={opt} className="mt-0.5 accent-emerald-700" onChange={trackChange(x.key, "checkboxes")} />
              <span>{opt}</span>
            </label>
          ))}
        </div>
      );
    }
    if (x.type === "tel") {
      return (
        <Input
          data-testid={`form-extra-${x.key}`}
          type="tel"
          name={x.key}
          required={x.required}
          placeholder={x.placeholder}
          className={inputCls}
        />
      );
    }
    // default: text
    return (
      <Input
        data-testid={`form-extra-${x.key}`}
        name={x.key}
        required={x.required}
        placeholder={x.placeholder}
        className={inputCls}
      />
    );
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent data-testid="form-dialog" className="max-w-lg bg-[#FFFAF0] border-emerald-100 max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle data-testid="form-dialog-title" className="font-[Cormorant_Garamond] text-3xl text-emerald-950">{form.label}</DialogTitle>
          <DialogDescription className="font-[Outfit] text-slate-600">{form.blurb}</DialogDescription>
        </DialogHeader>
        {submitted ? (
          <div data-testid="form-confirmation" className="space-y-3 py-4">
            <div className="text-rose-600 text-[11px] uppercase tracking-[0.2em] font-[Outfit]">Confirmed</div>
            <h4 className="font-[Cormorant_Garamond] text-2xl text-emerald-950">Thank you — your submission was received.</h4>
            <p className="font-[Outfit] text-slate-700 text-sm">
              A confirmation has been sent to your email. Our team will be in touch with next steps.
            </p>
            <Button onClick={() => handleOpenChange(false)} className="rounded-full bg-emerald-800 text-white hover:bg-emerald-900 font-[Outfit]">
              Close
            </Button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4 pt-2">
            {splitName ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="first_name" className="font-[Outfit] text-xs uppercase tracking-[0.15em] text-emerald-900">First Name *</Label>
                  <Input data-testid="form-first-name" name="first_name" required className="mt-1 bg-white border-emerald-200 focus-visible:ring-emerald-500/50" />
                </div>
                <div>
                  <Label htmlFor="last_name" className="font-[Outfit] text-xs uppercase tracking-[0.15em] text-emerald-900">Last Name *</Label>
                  <Input data-testid="form-last-name" name="last_name" required className="mt-1 bg-white border-emerald-200 focus-visible:ring-emerald-500/50" />
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="name" className="font-[Outfit] text-xs uppercase tracking-[0.15em] text-emerald-900">Name *</Label>
                  <Input data-testid="form-name" name="name" required className="mt-1 bg-white border-emerald-200 focus-visible:ring-emerald-500/50" />
                </div>
                <div>
                  <Label htmlFor="email-top" className="font-[Outfit] text-xs uppercase tracking-[0.15em] text-emerald-900">Email *</Label>
                  <Input data-testid="form-email" id="email-top" type="email" name="email" required className="mt-1 bg-white border-emerald-200 focus-visible:ring-emerald-500/50" />
                </div>
              </div>
            )}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {splitName && (
                <div>
                  <Label htmlFor="email" className="font-[Outfit] text-xs uppercase tracking-[0.15em] text-emerald-900">Email *</Label>
                  <Input data-testid="form-email" type="email" name="email" required className="mt-1 bg-white border-emerald-200 focus-visible:ring-emerald-500/50" />
                </div>
              )}
              <div>
                <Label htmlFor="phone" className="font-[Outfit] text-xs uppercase tracking-[0.15em] text-emerald-900">{splitName ? "Best Telephone Contact Number *" : "Phone"}</Label>
                <Input data-testid="form-phone" type="tel" name="phone" required={splitName} className="mt-1 bg-white border-emerald-200 focus-visible:ring-emerald-500/50" />
              </div>
              {!splitName && (
                <div>
                  <Label htmlFor="organization" className="font-[Outfit] text-xs uppercase tracking-[0.15em] text-emerald-900">Organization</Label>
                  <Input data-testid="form-org" name="organization" className="mt-1 bg-white border-emerald-200 focus-visible:ring-emerald-500/50" />
                </div>
              )}
            </div>
            {(form.extras || []).filter(shouldShow).map((x) => (
              <div key={x.key} id={x.anchor || undefined}>
                <Label className="font-[Outfit] text-xs uppercase tracking-[0.15em] text-emerald-900">{x.label}</Label>
                {renderExtra(x)}
              </div>
            ))}
            {!hideMessage && (
              <div>
                <Label className="font-[Outfit] text-xs uppercase tracking-[0.15em] text-emerald-900">Message</Label>
                <Textarea data-testid="form-message" name="message" rows={4} className="mt-1 bg-white border-emerald-200 focus-visible:ring-emerald-500/50" />
              </div>
            )}
            {form.footnote && (
              <p data-testid="form-footnote" className="text-xs text-slate-600 font-[Outfit] italic leading-relaxed border-l-2 border-emerald-300 pl-3">
                {form.footnote}
              </p>
            )}
            <Button
              type="submit"
              data-testid="form-submit"
              disabled={submitting}
              className="w-full rounded-full bg-emerald-800 hover:bg-emerald-900 text-white h-11 font-[Outfit]"
            >
              {submitting ? "Sending…" : "Submit"}
            </Button>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default function FormsSection({ autoOpenFormType, onAutoOpenHandled }) {
  const [openForm, setOpenForm] = useState(null);
  const [lastAutoOpen, setLastAutoOpen] = useState(null);

  // Derive: when autoOpenFormType changes to a non-null value we haven't handled, open it.
  if (autoOpenFormType && autoOpenFormType !== lastAutoOpen) {
    const match = FORMS.find((f) => f.type === autoOpenFormType);
    if (match) {
      setLastAutoOpen(autoOpenFormType);
      setOpenForm(match);
      onAutoOpenHandled?.();
      // If URL has #admin-tasks, scroll the dialog to that section once it's mounted
      if (typeof window !== "undefined" && window.location.hash === "#admin-tasks") {
        setTimeout(() => {
          const el = document.getElementById("admin-tasks");
          if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
        }, 400);
      }
    }
  }
  return (
    <section id="forms" data-testid="forms-section" className="bg-white py-20 sm:py-24 border-t border-emerald-100">
      <div className="mx-auto max-w-7xl px-6 lg:px-10">
        <div className="max-w-2xl mb-10">
          <div className="text-[11px] uppercase tracking-[0.3em] text-emerald-800 font-[Outfit] font-bold">Get Involved</div>
          <h2 className="font-[Cormorant_Garamond] text-4xl sm:text-5xl text-emerald-950 mt-3 leading-tight">
            Choose how you&apos;ll show up
          </h2>
          <p className="text-slate-700 font-[Outfit] mt-3">
            Sponsor an entrepreneur, host a resource table, attend the celebration, or join us in 2027. Every form goes straight to our team.
          </p>
        </div>

        {/* Live volunteer signup count — social proof, only renders once at least 1 signup */}
        <div className="mb-7">
          <VolunteerCountBadge variant="banner" onJoin={() => setOpenForm(FORMS.find((f) => f.type === "volunteer"))} />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
          {FORMS.map((f) => (
            <button
              key={f.type}
              data-testid={`open-form-${f.type}`}
              onClick={() => setOpenForm(f)}
              className="text-left bg-white border border-emerald-200 hover:border-emerald-500 transition-colors p-6 rounded-lg"
            >
              <div className="text-[10px] uppercase tracking-[0.25em] text-rose-600 font-[Outfit] font-bold">Form</div>
              <div className="font-[Cormorant_Garamond] text-2xl text-emerald-950 mt-1">{f.label}</div>
              <p className="text-sm text-slate-600 font-[Outfit] mt-2 leading-relaxed">{f.blurb}</p>
              <div className="text-emerald-700 mt-4 text-sm font-[Outfit] font-semibold">Open form →</div>
            </button>
          ))}
        </div>
      </div>
      <FormDialog open={!!openForm} onOpenChange={(v) => !v && setOpenForm(null)} form={openForm} />
    </section>
  );
}
